import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Alert, Button, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

type TurnstileApi = {
    render: (container: HTMLElement, options: Record<string, unknown>) => string
    reset: (id: string) => void
    remove: (id: string) => void
}
let loading: Promise<TurnstileApi> | undefined
const browserApi = () => (window as Window & { turnstile?: TurnstileApi }).turnstile
function loadTurnstile() {
    if (browserApi()) return Promise.resolve(browserApi()!)
    if (!loading) {
        loading = new Promise<TurnstileApi>((resolve, reject) => {
            const script = document.createElement('script')
            const fail = () => {
                clearTimeout(timer)
                script.remove()
                reject(new Error('Turnstile unavailable'))
            }
            const timer = setTimeout(fail, 15000)
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
            script.async = true
            script.onerror = fail
            script.onload = () => {
                const api = browserApi()
                if (!api) return fail()
                // The async script is fully evaluated when its load event fires.
                // Turnstile.ready() is only supported for scripts without async/defer.
                clearTimeout(timer)
                resolve(api)
            }
            document.head.appendChild(script)
        }).catch((error) => {
            loading = undefined
            throw error
        })
    }
    return loading
}

type TurnstileCheckProps = {
    siteKey: string
    action?: 'public_form' | 'register'
    appearance: 'light' | 'dark'
    locale: string
    resetKey: number
    showCheckingStatus?: boolean
    onToken: (token: string | undefined) => void
}
export function TurnstileCheck({
    siteKey,
    action = 'public_form',
    appearance,
    locale,
    resetKey,
    showCheckingStatus = true,
    onToken,
}: TurnstileCheckProps) {
    const { t } = useTranslation()
    const container = useRef<HTMLDivElement>(null)
    const widget = useRef<{ api: TurnstileApi; id: string }>(undefined)
    const callback = useRef(onToken)
    callback.current = onToken
    const [compact, setCompact] = useState(true)
    const [widgetVisible, setWidgetVisible] = useState(false)
    const [verified, setVerified] = useState(false)
    const [failed, setFailed] = useState(false)
    const [retry, setRetry] = useState(0)
    useLayoutEffect(() => {
        const element = container.current!
        const measure = () => {
            const bounds = element.getBoundingClientRect()
            setCompact(bounds.width < 300)
            setWidgetVisible(bounds.height > 0)
        }
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [])
    useEffect(() => {
        let cancelled = false
        setFailed(false)
        setVerified(false)
        callback.current(undefined)
        loadTurnstile()
            .then((api) => {
                if (cancelled || !container.current) return
                const fail = () => {
                    callback.current(undefined)
                    setVerified(false)
                    setFailed(true)
                }
                const id = api.render(container.current, {
                    sitekey: siteKey,
                    action,
                    theme: appearance,
                    language: locale === 'nl_NL' ? 'nl' : 'en',
                    size: compact ? 'compact' : 'flexible',
                    appearance: 'interaction-only',
                    'response-field': false,
                    callback: (token: string) => {
                        setFailed(false)
                        setVerified(true)
                        callback.current(token)
                    },
                    'expired-callback': fail,
                    'timeout-callback': fail,
                    'error-callback': fail,
                })
                widget.current = { api, id }
            })
            .catch(() => {
                if (!cancelled) {
                    callback.current(undefined)
                    setVerified(false)
                    setFailed(true)
                }
            })
        return () => {
            cancelled = true
            if (widget.current) {
                widget.current.api.remove(widget.current.id)
                widget.current = undefined
            }
            callback.current(undefined)
        }
    }, [siteKey, action, appearance, locale, compact, retry])
    useEffect(() => {
        setVerified(false)
        callback.current(undefined)
        if (widget.current) {
            setFailed(false)
            widget.current.api.reset(widget.current.id)
        }
    }, [resetKey])
    return (
        <div className={failed || (!verified && (showCheckingStatus || widgetVisible)) ? 'mb-4' : undefined}>
            <div ref={container} />
            {showCheckingStatus && !failed && !verified && (
                <Typography.Text
                    type="secondary"
                    role="status"
                >
                    {t('Checking security…', { lng: locale })}
                </Typography.Text>
            )}
            {failed && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('Verification failed. Please retry the security check.', { lng: locale })}
                    action={
                        <Button onClick={() => setRetry((value) => value + 1)}>{t('Retry', { lng: locale })}</Button>
                    }
                />
            )}
        </div>
    )
}
